import { createTheme } from "@mui/material/styles";

/**
 * Aeronet Brain canonical theme — THE single source of colour/type/radius.
 *
 * - brainTokens: raw values. Import in sx / styled code. Never hardcode hex.
 * - brainTheme: MUI createTheme() from those tokens. Applied once in MuiAppShell.
 * - brainCssVars: same values as CSS custom properties (--kt-*, --font-*, …)
 *   applied at boot so leftover kt-* CSS / public pages share the palette.
 *
 * Do not nest ThemeProvider on pages under MuiAppShell. Pricing sheet is the
 * only intentional exception (renders outside the shell for print).
 */
export const brainTokens = {
  color: {
    // teal family
    teal: "#0f8f8f",
    tealHover: "#0c7a7a",
    tealInk: "#0c7a7a",
    tealInk2: "#0e8c8c",
    tealTint: "#e4f2f0", // avatar bg, active nav/tab bg, badges
    tealTint2: "#eef7f6", // AI/agent panel background
    tealTintBorder: "#d3e7e5", // AI panel border
    // AgentPanel internals — were raw hexes in the component until the D1
    // Stage 2 adversarial review; same values, now bound to the token set so
    // "no raw hex in components" holds on the AI surface too.
    tealAvatarBg: "#d9efec", // AgentPanel sparkle avatar background
    tealStamp: "#86b8b8", // AgentPanel freshness stamp text
    tealBodyInk: "#1f3a3a", // AgentPanel body text
    // ink / text
    ink: "#1c2024",
    ink2: "#2a3038",
    ink3: "#3a4048",
    ink4: "#4b525a",
    muted: "#6b727a",
    muted2: "#8a9099",
    faint: "#9aa1a9", // uppercase labels, meta
    faint2: "#a4abb2",
    placeholder: "#c3c8cd", // em-dash empty values
    // borders / surfaces
    border: "#e4e7ea",
    borderLight: "#eceff1",
    borderLight2: "#f0f2f4",
    borderLight3: "#f2f4f6",
    hoverBorder: "#c7d6d6", // card hover border (SectorCard/AreaCard)
    dropBorder: "#d7dce0", // file drop-zone idle border
    white: "#ffffff",
    /** White raised surfaces (cards, paper). Not the page canvas. */
    pageBg: "#ffffff",
    /** App main canvas — a shade lighter than the sidebar so white cards sit softly. */
    canvas: "#f9fbfb",
    /** Left nav — the cool grey from the pre-MUI / kt shell. */
    sidebarBg: "#f6f8f9",
    panelBg: "#fafbfc",
    panelBg2: "#f7f9fa",
    panelBg3: "#f8fafb",
    topBar: "#0a0d10",
    topBarText: "#ffffff", // white top-bar foreground (wordmark, help/logout icons)
    sidebarBorder: "#e7eaed",
    // status: success
    successText: "#1c7a49",
    successText2: "#2f9e6f", // finance reconcile matched / cost-down
    successBg: "#e7f5ec",
    successBg2: "#e7f4ee",
    successBorder: "#c6e6d5",
    successDot: "#25a35f",
    // status: warning
    warnText: "#c47519",
    warnText2: "#d1801f",
    warnText3: "#b8791f", // finance suggested / mismatch
    warnBg: "#fbe9d3",
    warnBg2: "#fbefd2",
    warnBg3: "#f8efdd",
    warnBg4: "#fdf9ef", // suggested band select fill
    warnBorder: "#ecd9ac",
    warnDot: "#e08a2b",
    // status: danger
    dangerText: "#c23a3a",
    dangerText2: "#cf4040",
    dangerText3: "#d0524a", // finance cost-up / unset band
    dangerBg: "#fbe9e9",
    dangerBg2: "#fef6f5", // unset band select fill
    dangerBorder: "#f0d2d2",
    dangerBorder2: "#f0c9c6",
    dangerDot: "#e05650", // brighter danger accent for dots/spines — the missing sibling to successDot/warnDot
    // finance band confirmed select
    bandConfirmedBg: "#effafa",
    bandConfirmedBorder: "#bfe0e0",
    // bill-split bar shades (Where the bill goes)
    billShared: "#3aa8a8",
    billInternal: "#8fcccc",
    billDead: "#c9d3d3",
    // canvas / card (finance reorg surfaces) — canvas/sidebarBg defined above
    cardBorder: "#eef3f3",
    labelMuted: "#8a9a9a",
    captionFaint: "#b0bcbc",
    // reconcile not-recorded
    neutralPillBg: "#f4f7f7",
    neutralPillFg: "#9aa8a8",
    // citation chip
    citeText: "#0c7a7a",
    citeBg: "#eef6f6",
    citeBorder: "#d6eaea",
    // explain chip (ghost)
    explainText: "#3d8f8f",
    explainBg: "#eaf5f4",
    explainBorder: "#cfe6e4",
    // status pill text (StatusPill) — distinct fg tuning from the *Text tokens
    // above for contrast against the pill's own tint backgrounds
    pillWarnText: "#b06a1a",
    pillDangerText: "#a5302f",
    pillTealBg: "#dff0f0",
    pillTealText: "#0d7373",
    pillNeutralBg: "#eef0f2",
    pillNeutralText: "#606770",
    // brand dots on top bar (3-dot cluster: teal apex, red + orange base)
    brandOrange: "#e3722e",
    brandTeal: "#12b3b3",
    brandRed: "#d6342c",
  },
  font: {
    sans: "'DM Sans', system-ui, sans-serif",
    mono: "'DM Mono', ui-monospace, monospace",
  },
  // Avatar tint palette (bg, text) for name-hashed initials avatars.
  avatarPalette: [
    ["#cfeceb", "#0a7d7f"], ["#e3d2c9", "#8a5a44"], ["#d7dde8", "#5c6b85"],
    ["#ece2c4", "#8a6d1f"], ["#e6d6e1", "#8a4e74"], ["#d6e4dc", "#3f7a5c"],
  ] as [string, string][],
  radius: {
    card: 8,
    button: 7,
    chip: 6,
    pill: 999,
    avatar: 14,
    panel: 10,
    widget: 16,
  },
  layout: {
    contentMaxWidth: 1560,
    // top / right / bottom / left — left stays tight to the nav; wide screens blank on the right
    contentPadding: "26px 40px 80px 24px",
    sidebarWidth: 236,
    topBarHeight: 70,
  },
  shadow: {
    widget: "0 20px 54px rgba(15,90,90,.22)",
    fab: "0 12px 30px rgba(15,120,120,.32)",
    card: "0 1px 2px rgba(20,40,38,0.04)",
    heroCard: "0 3px 12px rgba(15,143,143,.12)",
    /** Board issue drawer — left-biased soft lift (Screen 3). */
    drawer: "-10px 0 28px rgba(16,24,32,.08)",
    /**
     * Menus / selects / popovers. These FLOAT over the page, so they are the one
     * place the flat-surface rule has to give: with `shadows` blanked and
     * MuiPaper forced to none, every dropdown rendered as white-on-white and
     * dissolved into the content behind it.
     */
    menu: "0 10px 28px rgba(16,24,32,.16), 0 2px 6px rgba(16,24,32,.08)",
  },
} as const;

const c = brainTokens.color;

export const brainTheme = createTheme({
  palette: {
    primary: { main: c.teal, dark: c.tealHover, contrastText: "#fff" },
    background: { default: c.canvas, paper: c.pageBg },
    text: { primary: c.ink, secondary: c.muted },
    divider: c.border,
    success: { main: c.successText, light: c.successBg },
    warning: { main: c.warnText, light: c.warnBg },
    error: { main: c.dangerText, light: c.dangerBg },
  },
  typography: {
    fontFamily: brainTokens.font.sans,
    // Body defaults per token table: 13-14px / 400-500 / line-height ~1.6.
    fontSize: 14,
  },
  shape: { borderRadius: brainTokens.radius.card },
  // Flat surfaces — only the widget/fab carry shadows (applied via sx).
  shadows: [
    "none",
    brainTokens.shadow.card,
    ...Array(23).fill("none"),
  ] as unknown as import("@mui/material/styles").Theme["shadows"],
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 500, borderRadius: brainTokens.radius.button },
        contained: {
          fontWeight: 600,
          backgroundColor: c.teal,
          boxShadow: "none",
          "&:hover": { backgroundColor: c.tealHover, boxShadow: "none" },
        },
        outlined: {
          borderColor: c.dropBorder,
          color: c.ink3,
          "&:hover": { backgroundColor: c.sidebarBg, borderColor: c.dropBorder },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: brainTokens.radius.chip },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: "none", backgroundImage: "none" },
        elevation1: { boxShadow: brainTokens.shadow.card },
      },
    },
    // Floating surfaces opt back IN to elevation — a dropdown with no shadow and
    // no border is indistinguishable from the page. Overriding the Popover slot
    // covers Menu and Select together, since both render through Popover.
    MuiPopover: {
      styleOverrides: {
        paper: {
          border: `1px solid ${c.border}`,
          borderRadius: brainTokens.radius.card,
          boxShadow: brainTokens.shadow.menu,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: `1px solid ${c.border}`,
          borderRadius: brainTokens.radius.card,
          boxShadow: brainTokens.shadow.menu,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 13,
          "&.Mui-selected": {
            backgroundColor: c.tealTint2,
            color: c.tealInk,
            fontWeight: 600,
            "&:hover": { backgroundColor: c.tealTint },
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { variant: "outlined" },
      styleOverrides: {
        root: { border: `1px solid ${c.border}`, borderRadius: brainTokens.radius.card, boxShadow: "none" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          minHeight: 44,
          fontSize: 14,
          color: c.muted,
          padding: "11px 16px",
          "&.Mui-selected": { color: c.tealInk, fontWeight: 600 },
          "&:hover": { color: c.ink },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44, borderBottom: `1px solid ${c.border}` },
        indicator: { backgroundColor: c.teal, height: 3 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: c.muted2,
          backgroundColor: c.panelBg2,
          borderBottom: `1px solid ${c.borderLight}`,
        },
        body: { fontSize: 13, borderBottom: `1px solid ${c.borderLight3}` },
      },
    },
  },
});
